import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  addHours,
  eachDayOfInterval,
  endOfDay,
  // eslint-disable-next-line no-restricted-imports
  format,
  isAfter,
  isBefore,
  isWithinInterval,
  parseISO,
  startOfDay,
} from 'date-fns';
import { useState } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  defineMessages,
} from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { EmptyState } from '@sorare/core/src/components/EmptyState';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { fantasy, glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { getSafePreviousLocation } from '@sorare/core/src/lib/navigation/getSafePreviousLocation';

import { WithFixtureHeaderLayout } from '#src/layouts/WithFixtureHeaderLayout/index.tsx';

import { MatchCenterGame } from './MatchCenterGame/index.tsx';
import type {
  MatchCenterPage_so5Fixture,
  MatchCenterPaginatedQuery,
  MatchCenterPaginatedQueryVariables,
} from './__generated__/index.graphql.ts';
import emptyIcon from './assets/empty_icon.svg';

const messages = defineMessages({
  noneOfYourPlayersPlaying: {
    defaultMessage: 'None of your players are playing a match on this day.',
  },
  noneOfYourPlayersPlayingBefore: {
    defaultMessage:
      'None of your players are playing a match on this day before {time}.',
  },
  noneOfYourPlayersPlayingAfter: {
    defaultMessage:
      'None of your players are playing a match on this day after {time}.',
  },
});

const MATCH_CENTER_PAGINATED_QUERY = gql`
  query MatchCenterPaginatedQuery(
    $slug: String!
    $fromDate: ISO8601DateTime!
    $toDate: ISO8601DateTime!
    $after: String
    $myGamesOnly: Boolean!
  ) {
    so5 {
      so5Fixture(slug: $slug) {
        slug
        sport
        fixtureGamesWithPlayerGameScoresPreview(
          first: 20
          after: $after
          fromDate: $fromDate
          toDate: $toDate
          myGamesOnly: $myGamesOnly
        ) {
          nodes {
            ...MatchCenterGame_gameWithPlayerGameScoresPreview
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${MatchCenterGame.fragments.gameWithPlayerGameScoresPreview}
` as TypedDocumentNode<
  MatchCenterPaginatedQuery,
  MatchCenterPaginatedQueryVariables
>;

const ContentWrapper = styled.div`
  padding: var(--double-unit) 0;
`;

type SingleFixtureProps = {
  so5Fixtures?: never;
  so5Fixture: MatchCenterPage_so5Fixture;
  withAllGames?: boolean;
  withFixtureHeader?: boolean;
  onClickPlayerGameScore?: (
    playerGameScoreId: string,
    playerSlug: string
  ) => void;
  onClickGame: (gameId: string, myPlayerSlugs?: string[]) => void;
};

type MultipleFixturesProps = {
  so5Fixture?: never;
  so5Fixtures: MatchCenterPage_so5Fixture[];
  withAllGames?: boolean;
  withFixtureHeader?: boolean;
  onClickPlayerGameScore?: (
    playerGameScoreId: string,
    playerSlug: string
  ) => void;
  onClickGame: (gameId: string, myPlayerSlugs?: string[]) => void;
};

type Props = SingleFixtureProps | MultipleFixturesProps;

export const MatchCenterPage = ({
  onClickGame,
  so5Fixture,
  so5Fixtures,
  withAllGames = false,
  onClickPlayerGameScore,
  withFixtureHeader = true,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();
  const { generateSportContextPath, sport } = useSportContext();

  const [myGamesOnly, setMyGamesOnly] = useState(!withAllGames);
  const [selectedDay, setSelectedDay] = useState<string>();

  const startFixture = so5Fixture ?? so5Fixtures[0];
  const endFixture = so5Fixture ?? so5Fixtures[so5Fixtures.length - 1];

  const safePreviousLocation = getSafePreviousLocation(
    generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
      params: {
        eventType: startFixture.type.toLowerCase(),
        fixture: startFixture.slug,
      },
    }),
    -1
  );

  const fixtureDays = eachDayOfInterval({
    start: startFixture.startDate,
    end: endFixture.endDate,
  }).map(date => format(date, 'yyyy-MM-dd'));

  const today = new Date();
  if (!selectedDay && fixtureDays.length) {
    const isTodayBeforeFixtures = isBefore(today, startFixture.startDate);
    const isTodayAfterFixtures = isAfter(today, endFixture.endDate);
    const isTodayWithinFixtures =
      isAfter(today, startFixture.startDate) &&
      isBefore(today, endFixture.endDate);

    if (isTodayWithinFixtures) {
      setSelectedDay(format(today, 'yyyy-MM-dd'));
    } else if (isTodayBeforeFixtures) {
      setSelectedDay(fixtureDays[0]);
    } else if (isTodayAfterFixtures) {
      setSelectedDay(fixtureDays[fixtureDays.length - 1]);
    }
  }

  const fixtureWithinInterval =
    selectedDay &&
    so5Fixtures?.find(fixture => {
      return isWithinInterval(parseISO(selectedDay!), {
        start: startOfDay(fixture.startDate),
        end: endOfDay(fixture.endDate),
      });
    });

  const selectedFixture = fixtureWithinInterval || so5Fixture;

  const selectedDayStart = selectedDay
    ? startOfDay(parseISO(selectedDay))
    : null;
  const selectedDayEnd = selectedDayStart
    ? addHours(selectedDayStart, 24)
    : null;

  const { data, loading, loadMore } = usePaginatedQuery(
    MATCH_CENTER_PAGINATED_QUERY,
    {
      variables: {
        slug: selectedFixture?.slug || startFixture.slug,
        toDate: selectedDayEnd!,
        fromDate: selectedDayStart!,
        myGamesOnly,
      },
      connection: 'GameWithPlayerGameScoresPreviewConnection',
      skip: !selectedDay,
    }
  );

  const { nodes = [], pageInfo } =
    data?.so5.so5Fixture?.fixtureGamesWithPlayerGameScoresPreview || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, { after: pageInfo?.endCursor });
    },
    !!pageInfo?.hasNextPage,
    loading
  );

  let emptyMessage = messages.noneOfYourPlayersPlaying;
  if (selectedDay === fixtureDays[0]) {
    emptyMessage = messages.noneOfYourPlayersPlayingAfter;
  }
  if (selectedDay === fixtureDays[fixtureDays.length - 1]) {
    emptyMessage = messages.noneOfYourPlayersPlayingBefore;
  }

  return (
    <ConditionalWrapper Wrapper={Container} wrap={!withFixtureHeader}>
      <ConditionalWrapper
        wrap={withFixtureHeader}
        Wrapper={WithFixtureHeaderLayout}
        props={{
          so5Fixture: selectedFixture || startFixture,
          title: (
            <FormattedMessage
              {...(sport === Sport.FOOTBALL
                ? fantasy.matchCenter
                : fantasy.gameCenter)}
            />
          ),
          children: null,
          backTo: safePreviousLocation,
          action: (
            <TabBar
              value={myGamesOnly.toString()}
              fullWidth
              variant="segmented"
            >
              {[true, false].map(value => (
                <Tab
                  key={value.toString()}
                  value={value.toString()}
                  onClick={() => setMyGamesOnly(value)}
                >
                  <FormattedMessage
                    {...(value ? navLabels.myCards : glossary.all)}
                  />
                </Tab>
              ))}
            </TabBar>
          ),
        }}
      >
        <ContentWrapper>
          <Vertical gap={3}>
            <TabBar
              value={selectedDay}
              onValueChange={setSelectedDay}
              fullWidth
              variant="flat"
            >
              {fixtureDays.map(day => (
                <Tab key={day} value={day}>
                  <FormattedDate
                    value={day}
                    weekday="short"
                    day="2-digit"
                    month="short"
                    timeZone="UTC"
                  />
                </Tab>
              ))}
            </TabBar>
            <Vertical gap={isLaptopAndAbove ? 0 : 2}>
              {!loading && !nodes?.length ? (
                <EmptyState
                  title={<FormattedMessage defaultMessage="No Games" />}
                  message={
                    <FormattedMessage
                      {...emptyMessage}
                      values={{
                        time: (
                          <FormattedTime
                            value={
                              selectedDay === fixtureDays[0]
                                ? startFixture.startDate
                                : endFixture.endDate
                            }
                          />
                        ),
                      }}
                    />
                  }
                  imageUrl={emptyIcon}
                />
              ) : (
                <>
                  {nodes?.map(gameWithPgsPreview => (
                    <MatchCenterGame
                      key={gameWithPgsPreview.game.id}
                      gameWithPlayerGameScores={gameWithPgsPreview}
                      onClickGame={onClickGame}
                      onClickPlayerGameScore={onClickPlayerGameScore}
                    />
                  ))}
                  <InfiniteScrollLoader />
                </>
              )}
            </Vertical>
          </Vertical>
        </ContentWrapper>
      </ConditionalWrapper>
    </ConditionalWrapper>
  );
};

MatchCenterPage.fragments = {
  so5Fixture: gql`
    fragment MatchCenterPage_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
      type
      ...WithFixtureHeaderLayout_so5Fixture
    }
    ${WithFixtureHeaderLayout.fragments.so5Fixture}
  ` as TypedDocumentNode<MatchCenterPage_so5Fixture>,
};
